import React, { useState, useEffect, useRef, useContext } from 'react'
import { Dialog } from 'primereact/dialog'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Badge } from 'primereact/badge'
import { Button } from 'primereact/button'
import { OverlayPanel } from 'primereact/overlaypanel'
import { formatCurrency, formatPercentage } from '../shared/utils'
import server from '../../server'
import originsDirectLogo from '../../assets/images/ORIGINS-DIRECT.png'
import originsDirectInstantLogo from '../../assets/images/ORIGINS-DIRECT-INSTANT.png'
import { AuthenticationContext } from '../../Auth/authentication.context'
import PaNavigatorDisclaimer from './PaNavigatorDisclaimer'

const buildManufacturerGroups = (combined, moleculeName) => {
  const mol = moleculeName && String(moleculeName).trim() ? String(moleculeName).trim() : ''
  const map = new Map()
  for (const p of combined) {
    const id = p.manufacturer?.id != null ? p.manufacturer.id : 'unknown'
    const mname = p.manufacturer?.name || 'Unknown'
    if (!map.has(id)) {
      map.set(id, { manufacturerId: id, manufacturerName: mname, items: [] })
    }
    map.get(id).items.push(p)
  }
  return [...map.values()]
    .map((g) => {
      const items = [...g.items].sort((a, b) => (b?.paPercentage || 0) - (a?.paPercentage || 0))
      const bestPa = items.length ? Math.max(...items.map((i) => i.paPercentage || 0)) : 0
      const moleculeLabel = mol ? `${g.manufacturerName}-${mol}` : g.manufacturerName
      const hasCurrentProduct = items.some((i) => i.isCurrentProduct)
      const manufacturerOdPaAdvantage = items.length ? (items[0].manufacturerOdPaAdvantage ?? 0) : 0
      const showPaAdvantageStar = manufacturerOdPaAdvantage > 1
      return {
        ...g,
        items,
        groupKey: `mfr-${g.manufacturerId}`,
        bestPa,
        moleculeLabel,
        hasCurrentProduct,
        manufacturerOdPaAdvantage,
        showPaAdvantageStar,
      }
    })
    .sort((a, b) => b.bestPa - a.bestPa)
}

const TIER_DISPLAY = {
  gold: {
    subtitle: 'Highest PA advantage',
    icon: 'mdi-podium-gold',
  },
  silver: {
    subtitle: 'Second highest PA',
    icon: 'mdi-podium-silver',
  },
  bronze: {
    subtitle: 'Additional options',
    icon: 'mdi-podium-bronze',
  },
}

/** Legacy 33/66 percentile split — kept for reference, not used. */
// eslint-disable-next-line no-unused-vars
const splitManufacturerTiersByPercentile = (groups) => {
  const total = groups.length
  if (total === 0) {
    return { gold: [], silver: [], bronze: [] }
  }
  const goldEnd = Math.ceil(total * 0.33)
  const silverEnd = Math.ceil(total * 0.66)
  return {
    gold: groups.slice(0, goldEnd),
    silver: groups.slice(goldEnd, silverEnd),
    bronze: groups.slice(silverEnd),
  }
}

/** One gold (highest PA), one silver (second), all other manufacturers bronze. */
const splitManufacturerTiersByRank = (groups) => {
  if (groups.length === 0) {
    return { gold: [], silver: [], bronze: [] }
  }
  return {
    gold: groups.slice(0, 1),
    silver: groups.length > 1 ? groups.slice(1, 2) : [],
    bronze: groups.length > 2 ? groups.slice(2) : [],
  }
}

const AlternativesModal = ({ visible, setVisible, product, onReplace, replaceMode = false, includeOdPaAdvantage = false, onAddToCart, showGuidanceDisclaimer = false }) => {
  const { user } = useContext(AuthenticationContext)
  const isAdmin = user?.role === 'admin'
  const [alternatives, setAlternatives] = useState({ gold: [], silver: [], bronze: [] })
  const [loading, setLoading] = useState(false)
  const [expandedByTier, setExpandedByTier] = useState({ gold: null, silver: null, bronze: null })
  const odStarTipRef = useRef(null)
  const odStarHideTimerRef = useRef(null)

  const clearOdStarHideTimer = () => {
    if (odStarHideTimerRef.current) {
      clearTimeout(odStarHideTimerRef.current)
      odStarHideTimerRef.current = null
    }
  }

  const scheduleHideOdStarTip = () => {
    clearOdStarHideTimer()
    odStarHideTimerRef.current = setTimeout(() => {
      odStarTipRef.current?.hide()
      odStarHideTimerRef.current = null
    }, 200)
  }

  const showOdStarTip = (e) => {
    if (!includeOdPaAdvantage) return
    clearOdStarHideTimer()
    odStarTipRef.current?.show(e, e.currentTarget)
  }

  useEffect(() => () => clearOdStarHideTimer(), [])

  const odStarTipLogo =
    typeof localStorage !== 'undefined' && localStorage.getItem('module') === 'Origins Direct Instant'
      ? originsDirectInstantLogo
      : originsDirectLogo

  useEffect(() => {
    if (!visible) {
      setExpandedByTier({ gold: null, silver: null, bronze: null })
      clearOdStarHideTimer()
      odStarTipRef.current?.hide()
    }
  }, [visible])

  useEffect(() => {
    if (visible && product) {
      fetchAlternatives()
    } else {
      setAlternatives({ gold: [], silver: [], bronze: [] })
    }
  }, [visible, product])

  const fetchAlternatives = async () => {
    if (!product || !product.drugIdentificationNo) {
      return
    }

    setLoading(true)
    setExpandedByTier({ gold: null, silver: null, bronze: null })
    try {
      const response = await server.get(`/products/alternatives`, {
        params: {
          productId: product.id,
          drugIdentificationNo: product.drugIdentificationNo,
          currentPaPercentage: product.paPercentage || 0
        },
        headers: {
          'x-system-name': includeOdPaAdvantage ? 'Origins Direct' : 'Origins Management',
        },
      })
      const data = response.data || { current: null, gold: [], silver: [], bronze: [] }

      const backendGold = data.gold || []
      const backendSilver = data.silver || []
      const backendBronze = data.bronze || []
      const current = data.current || null
      const moleculeName = data.moleculeName != null ? data.moleculeName : null

      const allAlternatives = [...backendGold, ...backendSilver, ...backendBronze]
      if (allAlternatives.length === 0) {
        setAlternatives({ gold: [], silver: [], bronze: [] })
        return
      }

      const combined = current
        ? [{ ...current, isCurrentProduct: true }, ...allAlternatives]
        : [...allAlternatives]

      combined.sort((a, b) => (b?.paPercentage || 0) - (a?.paPercentage || 0))

      const manufacturerGroups = buildManufacturerGroups(combined, moleculeName)
      setAlternatives(splitManufacturerTiersByRank(manufacturerGroups))
    } catch (error) {
      console.error('Error fetching alternatives:', error)
      setAlternatives({ gold: [], silver: [], bronze: [] })
    } finally {
      setLoading(false)
    }
  }

  const productDescriptionBodyTemplate = (rowData) => {
    const isCurrent = rowData.isCurrentProduct
    return (
      <div style={{ fontWeight: '500', color: '#333' }}>
        {rowData.itemDescription || rowData.item_description}
        {isCurrent && (
          <Badge value='Current' severity='info' style={{ marginLeft: '8px' }} />
        )}
      </div>
    )
  }

  const strengthBodyTemplate = (rowData) => {
    let strength = rowData.strength || rowData.drugStrength
    const desc = rowData.itemDescription || rowData.item_description
    if (!strength && desc) {
      const strengthMatch = desc.match(/\[Strength:\s*([^\]]+)\]/i)
      if (strengthMatch) strength = strengthMatch[1].trim()
    }
    return <div style={{ color: '#333' }}>{strength || 'N/A'}</div>
  }

  const dinBodyTemplate = (rowData) => {
    const din = rowData.drugIdentificationNo
    if (din == null || din === '') return din
    return <span style={{ fontFamily: 'monospace' }}>{String(din).padStart(8, '0')}</span>
  }

  const paPercentageBodyTemplate = (rowData) => {
    const alternativePa = rowData.paPercentage || 0

    return (
      <Badge
        value={formatPercentage(alternativePa)}
        severity='info'
      />
    )
  }

  const priceBodyTemplate = (rowData) => {
    const alternativePrice = rowData.sellingPrice || 0

    return (
      <div style={{ fontWeight: '600', color: '#333' }}>
        {formatCurrency(alternativePrice)}
      </div>
    )
  }

  const manufacturerBodyTemplate = (rowData) => {
    return (
      <div style={{ color: '#666' }}>
        {rowData.manufacturer?.name || 'N/A'}
      </div>
    )
  }

  const rowClassName = (rowData) => {
    if (rowData.isCurrentProduct) {
      return 'current-product-row'
    }
    return ''
  }

  const isTierRowExpanded = (tierKey, rowData) =>
    !!expandedByTier[tierKey]?.[rowData.groupKey]

  const toggleTierRowExpansion = (tierKey, rowData) => {
    setExpandedByTier((prev) => {
      const key = rowData.groupKey
      if (prev[tierKey]?.[key]) {
        return { ...prev, [tierKey]: null }
      }
      return { ...prev, [tierKey]: { [key]: true } }
    })
  }

  const createManufacturerRowBody = (tierKey) => (rowData) => {
    const expanded = isTierRowExpanded(tierKey, rowData)
    return (
      <div className='d-flex align-items-center gap-2 alternatives-mfr-row'>
        <button
          type='button'
          className='alternatives-mfr-label-btn'
          onClick={() => toggleTierRowExpansion(tierKey, rowData)}
          aria-expanded={expanded}
        >
          {rowData.moleculeLabel}
        </button>
        {rowData.showPaAdvantageStar && (
          <span
            className='d-inline-flex align-items-center alternatives-od-star-hover-target'
            onMouseEnter={includeOdPaAdvantage ? showOdStarTip : undefined}
            onMouseLeave={includeOdPaAdvantage ? scheduleHideOdStarTip : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            <i
              className='pi pi-star-fill alternatives-pa-advantage-star'
              title={
                includeOdPaAdvantage
                  ? undefined
                  : `Manufacturer PA advantage (Origins Direct): ${formatPercentage(rowData.manufacturerOdPaAdvantage || 0)}`
              }
              aria-label={
                includeOdPaAdvantage
                  ? `Get Best deal for this manufacturer by ordering from Origins Direct. Manufacturer PA advantage ${formatPercentage(rowData.manufacturerOdPaAdvantage || 0)}.`
                  : `Manufacturer PA advantage over one percent: ${formatPercentage(rowData.manufacturerOdPaAdvantage || 0)}`
              }
            />
          </span>
        )}
        {rowData.hasCurrentProduct && <Badge value='Current' severity='info' />}
        <Button
          type='button'
          label={expanded ? 'Hide Items' : 'Show Items >'}
          size='small'
          outlined
          className='alternatives-show-items-btn'
          onClick={() => toggleTierRowExpansion(tierKey, rowData)}
          aria-expanded={expanded}
        />
      </div>
    )
  }

  const bestPaBodyTemplate = (rowData) => (
    <Badge value={formatPercentage(rowData.bestPa || 0)} severity='info' />
  )

  const renderSkuDataTable = (items) => (
    <div className='alternatives-modal-sku-table'>
      <DataTable
        value={items}
        loading={loading}
        emptyMessage='No products in this group'
        showGridlines
        stripedRows
        rowClassName={rowClassName}
        sortable={false}
        removableSort={false}
        className='alternatives-modal-sku-datatable'
      >
      <Column field='retailUpcCode' header='UPC#' style={{ minWidth: '120px' }} />
      <Column header='Product Description' body={productDescriptionBodyTemplate} style={{ minWidth: '250px' }} />
      <Column header='Manufacturer' body={manufacturerBodyTemplate} style={{ minWidth: '150px' }} />
      <Column header='DIN/NPN' body={dinBodyTemplate} style={{ minWidth: '100px' }} />
      <Column header='Strength' body={strengthBodyTemplate} style={{ minWidth: '100px' }} />
      {isAdmin && <Column header='PA %' body={paPercentageBodyTemplate} style={{ minWidth: '90px' }} />}
      <Column header='Price' body={priceBodyTemplate} style={{ minWidth: '100px' }} />
      {replaceMode && onReplace && (
        <Column
          header='Action'
          body={(rowData) => (
            rowData.isCurrentProduct ? null : (
              <Button
                label='Replace'
                icon='pi pi-check'
                size='small'
                disabled={!!rowData.backlog}
                title={
                  rowData.backlog
                    ? 'This SKU is on backlog and cannot be used as a replacement.'
                    : undefined
                }
                onClick={() => {
                  onReplace(rowData)
                  setVisible(false)
                }}
              />
            )
          )}
          style={{ minWidth: '120px' }}
        />
      )}
      {onAddToCart && (
        <Column
          header='Add to Cart'
          body={(rowData) => (
            rowData.isCurrentProduct ? null : (
              <Button
                label='Add to Cart'
                disabled={!!rowData.backlog}
                className={rowData.backlog ? 'p-button-secondary opacity-50' : undefined}
                title={
                  rowData.backlog
                    ? 'This SKU is on backlog and cannot be added to the cart.'
                    : undefined
                }
                onClick={() => {
                  onAddToCart(rowData)
                  setVisible(false)
                }}
              />
            )
          )}
          style={{ minWidth: '140px' }}
        />
      )}
      </DataTable>
    </div>
  )

  const renderAlternativesTable = (tier, data, tierKey) => {
    if (data.length === 0) return null

    const display = TIER_DISPLAY[tierKey] || TIER_DISPLAY.gold

    return (
      <div className={`alternatives-tier-card alternatives-tier-card--${tierKey}`}>
        <div className={`alternatives-tier-ribbon alternatives-tier-ribbon--${tierKey}`}>
          <div className='alternatives-tier-ribbon-inner'>
            <div className='alternatives-tier-emblem' aria-hidden='true'>
              <span className={`mdi ${display.icon}`} />
            </div>
            <div className='alternatives-tier-ribbon-copy'>
              <span className='alternatives-tier-ribbon-kicker'>{display.subtitle}</span>
              <h5 className='alternatives-tier-ribbon-title mb-0'>{tier}</h5>
            </div>
          </div>
        </div>
        <div className='alternatives-tier-body'>
          <DataTable
            className='alternatives-manufacturer-tier'
            value={data}
            dataKey='groupKey'
            expandedRows={expandedByTier[tierKey]}
            onRowToggle={(e) => setExpandedByTier((prev) => ({ ...prev, [tierKey]: e.data }))}
            rowExpansionTemplate={(rowData) => renderSkuDataTable(rowData.items)}
            loading={loading}
            emptyMessage='No alternatives found'
            showGridlines
            stripedRows
          >
            <Column header='Manufacturer & molecule' body={createManufacturerRowBody(tierKey)} style={{ minWidth: '280px' }} />
            {isAdmin && <Column header='Best PA %' body={bestPaBodyTemplate} style={{ minWidth: '100px' }} />}
          </DataTable>
        </div>
      </div>
    )
  }

  const hasAlternatives =
    alternatives.gold.length > 0 ||
    alternatives.silver.length > 0 ||
    alternatives.bronze.length > 0

  return (
    <Dialog
      header={
        <div className='d-flex align-items-center gap-2'>
          <i className='pi pi-search' style={{ fontSize: '1.25rem' }} />
          <span>Better Value Alternatives</span>
        </div>
      }
      visible={visible}
      style={{ width: '95vw', maxWidth: '1400px' }}
      onHide={() => setVisible(false)}
      modal
      className='p-fluid'
      draggable={false}
      resizable={false}
    >
      {showGuidanceDisclaimer && <PaNavigatorDisclaimer className='mb-3' />}
      {loading ? (
        <div className='text-center p-5'>
          <i className='pi pi-spin pi-spinner' style={{ fontSize: '2rem', color: '#2196F3' }} />
          <p className='mt-3' style={{ color: '#666' }}>Finding better alternatives...</p>
        </div>
      ) : !hasAlternatives ? (
        <div className='text-center p-5'>
          <i className='pi pi-info-circle' style={{ fontSize: '3rem', color: '#ccc' }} />
          <p className='mt-3' style={{ color: '#666', fontSize: '1.1rem' }}>
            No alternatives found at this time.
          </p>
          <p style={{ color: '#999', fontSize: '0.9rem' }}>
            Check back later or try searching for a different product.
          </p>
        </div>
      ) : (
        <>
          <style>{`
            .alternatives-manufacturer-tier .p-datatable-row-expansion > td {
              padding: 0 !important;
              vertical-align: top;
            }
            .alternatives-manufacturer-tier .alternatives-modal-sku-table {
              width: 100%;
              margin: 0;
            }
            .alternatives-manufacturer-tier .alternatives-modal-sku-datatable.p-datatable {
              width: 100%;
              margin: 0;
              border-radius: 0;
            }
            .alternatives-manufacturer-tier .alternatives-modal-sku-datatable .p-datatable-wrapper {
              border-radius: 0;
            }
            .alternatives-manufacturer-tier .alternatives-modal-sku-datatable .p-datatable-table {
              border-radius: 0;
            }
            .alternatives-mfr-label-btn {
              background: none;
              border: none;
              padding: 0;
              font-weight: 600;
              color: #333;
              cursor: pointer;
              text-align: left;
            }
            .alternatives-mfr-label-btn:hover {
              color: #2196F3;
              text-decoration: underline;
            }
            .alternatives-mfr-row {
              flex-wrap: nowrap;
            }
            .alternatives-show-items-btn.p-button {
              width: auto;
              flex: 0 0 auto;
              font-size: 0.8125rem;
              padding: 0.25rem 0.5rem;
              white-space: nowrap;
            }
            .p-fluid .alternatives-show-items-btn.p-button {
              width: auto;
            }
            .alternatives-pa-advantage-star {
              color: #c9a227;
              font-size: 0.95rem;
              line-height: 1;
            }
            .alternatives-od-star-hover-target {
              cursor: help;
            }
            .alternatives-od-star-tip-panel.p-overlaypanel {
              max-width: 232px;
              border: none;
              box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
            }
            .alternatives-od-star-tip-panel.p-overlaypanel .p-overlaypanel-content {
              padding: 0;
            }
            .alternatives-od-star-tip-inner {
              background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
              border: 1px solid rgba(0, 0, 0, 0.08);
              border-radius: 10px;
              padding: 9px 10px;
            }
            .alternatives-od-star-tip-copy {
              margin: 0;
              font-size: 0.8125rem;
              line-height: 1.55;
              color: #212529;
              letter-spacing: 0.01em;
            }
            .alternatives-od-star-tip-logo-wrap {
              display: inline-flex;
              align-items: center;
              vertical-align: middle;
              margin-left: 2px;
            }
            .alternatives-od-star-tip-logo {
              height: 18px;
              width: auto;
              max-width: 88px;
              object-fit: contain;
              object-position: left center;
              display: block;
            }
            .alternatives-tier-card {
              position: relative;
              margin-bottom: 1.25rem;
              border-radius: 12px;
              overflow: hidden;
              font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              box-shadow:
                0 1px 2px rgba(15, 23, 42, 0.06),
                0 6px 20px rgba(15, 23, 42, 0.1);
            }
            .alternatives-tier-card::before {
              content: '';
              position: absolute;
              inset: 0;
              pointer-events: none;
              z-index: 0;
              background: linear-gradient(
                118deg,
                rgba(255, 255, 255, 0.42) 0%,
                rgba(255, 255, 255, 0.08) 28%,
                transparent 48%,
                rgba(0, 0, 0, 0.04) 72%,
                rgba(255, 255, 255, 0.12) 100%
              );
              mix-blend-mode: soft-light;
            }
            .alternatives-tier-card::after {
              content: '';
              position: absolute;
              inset: 0;
              pointer-events: none;
              z-index: 0;
              opacity: 0.35;
              background-image: repeating-linear-gradient(
                -12deg,
                transparent,
                transparent 3px,
                rgba(255, 255, 255, 0.04) 3px,
                rgba(0, 0, 0, 0.02) 4px
              );
            }
            .alternatives-tier-card > * {
              position: relative;
              z-index: 1;
            }
            .alternatives-tier-card--gold {
              background-color: #b8860b;
              background-image:
                radial-gradient(ellipse 90% 55% at 12% -8%, rgba(255, 248, 220, 0.9) 0%, transparent 52%),
                radial-gradient(ellipse 70% 45% at 92% 108%, rgba(107, 82, 6, 0.35) 0%, transparent 48%),
                linear-gradient(
                  168deg,
                  #f0d878 0%,
                  #e8c547 18%,
                  #d4af37 38%,
                  #c9a227 52%,
                  #daa520 68%,
                  #b8860b 85%,
                  #9a7209 100%
                );
              border: 1px solid rgba(154, 114, 9, 0.85);
              box-shadow:
                0 1px 2px rgba(15, 23, 42, 0.06),
                0 6px 20px rgba(120, 90, 10, 0.18),
                inset 0 1px 0 rgba(255, 248, 220, 0.55),
                inset 0 -2px 6px rgba(107, 82, 6, 0.2);
            }
            .alternatives-tier-card--silver {
              background-color: #8b95a3;
              background-image:
                radial-gradient(ellipse 85% 50% at 10% -5%, rgba(255, 255, 255, 0.95) 0%, transparent 50%),
                radial-gradient(ellipse 65% 40% at 95% 105%, rgba(71, 85, 105, 0.28) 0%, transparent 46%),
                linear-gradient(
                  168deg,
                  #eef1f5 0%,
                  #d8dee6 14%,
                  #c5ccd6 32%,
                  #b8c0ca 48%,
                  #c9d1da 62%,
                  #e2e8f0 78%,
                  #a8b2be 92%,
                  #8b95a3 100%
                );
              border: 1px solid rgba(107, 118, 132, 0.75);
              box-shadow:
                0 1px 2px rgba(15, 23, 42, 0.06),
                0 6px 20px rgba(71, 85, 105, 0.14),
                inset 0 1px 0 rgba(255, 255, 255, 0.7),
                inset 0 -2px 6px rgba(71, 85, 105, 0.15);
            }
            .alternatives-tier-card--bronze {
              background-color: #8b4513;
              background-image:
                radial-gradient(ellipse 88% 52% at 14% -6%, rgba(255, 228, 196, 0.75) 0%, transparent 50%),
                radial-gradient(ellipse 68% 42% at 90% 102%, rgba(80, 40, 12, 0.38) 0%, transparent 48%),
                linear-gradient(
                  168deg,
                  #e8b88a 0%,
                  #d4956a 16%,
                  #cd7f32 34%,
                  #b87333 50%,
                  #c9854a 66%,
                  #a8652a 82%,
                  #8b4513 100%
                );
              border: 1px solid rgba(139, 69, 19, 0.8);
              box-shadow:
                0 1px 2px rgba(15, 23, 42, 0.06),
                0 6px 20px rgba(139, 69, 19, 0.16),
                inset 0 1px 0 rgba(255, 228, 196, 0.45),
                inset 0 -2px 6px rgba(80, 40, 12, 0.22);
            }
            .alternatives-tier-ribbon {
              border-bottom: 1px solid rgba(26, 29, 41, 0.1);
            }
            .alternatives-tier-ribbon--gold {
              background: linear-gradient(
                180deg,
                rgba(240, 216, 120, 0.72) 0%,
                rgba(201, 162, 39, 0.58) 55%,
                rgba(154, 114, 9, 0.45) 100%
              );
              box-shadow: inset 0 1px 0 rgba(255, 248, 220, 0.35);
            }
            .alternatives-tier-ribbon--silver {
              background: linear-gradient(
                180deg,
                rgba(238, 241, 245, 0.85) 0%,
                rgba(197, 204, 214, 0.65) 55%,
                rgba(139, 149, 163, 0.5) 100%
              );
              box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
            }
            .alternatives-tier-ribbon--bronze {
              background: linear-gradient(
                180deg,
                rgba(232, 184, 138, 0.75) 0%,
                rgba(205, 127, 50, 0.58) 55%,
                rgba(139, 69, 19, 0.42) 100%
              );
              box-shadow: inset 0 1px 0 rgba(255, 228, 196, 0.3);
            }
            .alternatives-tier-ribbon-inner {
              display: flex;
              align-items: center;
              gap: 0.65rem;
              padding: 0.75rem 1rem;
            }
            .alternatives-tier-emblem {
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              line-height: 1;
            }
            .alternatives-tier-emblem .mdi {
              font-size: 1.25rem;
            }
            .alternatives-tier-card--gold .alternatives-tier-emblem .mdi {
              color: #7a5c08;
              filter: drop-shadow(0 1px 0 rgba(255, 248, 220, 0.6));
            }
            .alternatives-tier-card--silver .alternatives-tier-emblem .mdi {
              color: #4a5568;
              filter: drop-shadow(0 1px 0 rgba(255, 255, 255, 0.65));
            }
            .alternatives-tier-card--bronze .alternatives-tier-emblem .mdi {
              color: #6d3a12;
              filter: drop-shadow(0 1px 0 rgba(255, 228, 196, 0.5));
            }
            .alternatives-tier-ribbon-copy {
              min-width: 0;
            }
            .alternatives-tier-ribbon-kicker {
              display: block;
              font-size: 0.8125rem;
              font-weight: 500;
              color: #5a5e6b;
              margin-bottom: 0.1rem;
              line-height: 1.35;
            }
            .alternatives-tier-ribbon-title {
              font-size: 1.0625rem;
              font-weight: 600;
              color: #1a1d29;
              line-height: 1.3;
              letter-spacing: -0.01em;
            }
            .alternatives-tier-body {
              padding: 0;
              background: transparent;
            }
            .alternatives-tier-body .alternatives-manufacturer-tier.p-datatable {
              border: none;
              border-radius: 0;
              margin: 0;
              width: 100%;
              overflow: hidden;
              background: linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.97) 0%,
                rgba(252, 252, 253, 0.94) 100%
              );
              box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
            }
            .alternatives-tier-body .alternatives-manufacturer-tier.p-datatable .p-datatable-wrapper,
            .alternatives-tier-body .alternatives-manufacturer-tier.p-datatable .p-datatable-table {
              width: 100%;
              border-radius: 0;
            }
            .current-product-row {
              background-color: #e3f2fd !important;
              border-left: 4px solid #2196F3 !important;
            }
            .current-product-row:hover {
              background-color: #bbdefb !important;
            }
          `}</style>
          {renderAlternativesTable('Gold', alternatives.gold, 'gold')}
          {renderAlternativesTable('Silver', alternatives.silver, 'silver')}
          {renderAlternativesTable('Bronze', alternatives.bronze, 'bronze')}
          {includeOdPaAdvantage && (
            <OverlayPanel
              ref={odStarTipRef}
              dismissable={false}
              className='alternatives-od-star-tip-panel'
              baseZIndex={12000}
            >
              <div
                className='alternatives-od-star-tip-inner'
                onMouseEnter={clearOdStarHideTimer}
                onMouseLeave={scheduleHideOdStarTip}
              >
                <p className='alternatives-od-star-tip-copy'>
                  Get Best deal for this manufacturer by ordering from{' '}
                  <span className='alternatives-od-star-tip-logo-wrap'>
                    <img src={odStarTipLogo} alt='Origins Direct' className='alternatives-od-star-tip-logo' />
                  </span>
                  .
                </p>
              </div>
            </OverlayPanel>
          )}
        </>
      )}
    </Dialog>
  )
}

export default AlternativesModal
